import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";

const QuickAnswersBulkAudioModal = ({ open, onClose, onDone }) => {
  const [files, setFiles] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productId, setProductId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const fileInputRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => () => { isMounted.current = false; }, []);

  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setProductId("");
    setCategoryId("");
    setProgress({ done: 0, total: 0 });
    api.get("/products").then(({ data }) => {
      if (isMounted.current) setProducts(data || []);
    }).catch(() => {});
    api.get("/categories").then(({ data }) => {
      if (isMounted.current) setCategories(data || []);
    }).catch(() => {});
  }, [open]);

  const loadCategories = (pid) => {
    const url = pid ? `/categories?productId=${pid}` : "/categories";
    api.get(url).then(({ data }) => {
      if (isMounted.current) setCategories(data || []);
    }).catch(() => {});
  };

  const handleFilesChange = (e) => {
    const selected = Array.from(e.target.files);
    setFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      return [...prev, ...selected.filter(f => !existingNames.has(f.name))];
    });
    e.target.value = "";
  };

  const removeFile = (name) => setFiles(prev => prev.filter(f => f.name !== name));

  const stripExt = (name) => name.replace(/\.[^.]+$/, "");

  const handleUpload = async () => {
    if (!files.length) { toast.error("Selecione ao menos um arquivo"); return; }
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    let doneCount = 0;
    let errors = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("shortcut", stripExt(file.name));
        fd.append("message", "");
        fd.append("type", "audio");
        if (productId) fd.append("productId", productId);
        if (categoryId) {
          fd.append("categoryId", categoryId);
          const cat = categories.find(c => String(c.id) === String(categoryId));
          if (cat) fd.append("category", cat.name);
        }
        fd.append("media", file);
        await api.post("/quickAnswers", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (_) {
        errors++;
      }
      doneCount++;
      if (isMounted.current) setProgress({ done: doneCount, total: files.length });
    }
    setUploading(false);
    if (errors === 0) {
      toast.success(`${files.length} áudio(s) importado(s) com sucesso!`);
    } else {
      toast.warning(`${files.length - errors} importado(s), ${errors} com erro.`);
    }
    if (onDone) onDone();
    onClose();
  };

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AudiotrackIcon color="success" />
        Importar Múltiplos Áudios
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Selecione vários arquivos de áudio de uma vez. Cada arquivo será criado como uma Resposta Rápida
          com o nome do arquivo como atalho.
        </Typography>

        {products.length > 0 && (
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Produto (opcional)</InputLabel>
            <Select
              value={productId}
              label="Produto (opcional)"
              onChange={(e) => {
                setProductId(e.target.value);
                setCategoryId("");
                loadCategories(e.target.value);
              }}
              disabled={uploading}
            >
              <MenuItem value="">Nenhum produto</MenuItem>
              {products.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Categoria (opcional)</InputLabel>
          <Select
            value={categoryId}
            label="Categoria (opcional)"
            onChange={(e) => setCategoryId(e.target.value)}
            disabled={uploading}
          >
            <MenuItem value="">Sem categoria</MenuItem>
            {categories.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>

        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          multiple
          style={{ display: "none" }}
          onChange={handleFilesChange}
        />
        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          fullWidth
          sx={{ mb: 1.5 }}
        >
          Selecionar Áudios
        </Button>

        {files.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxHeight: 240, overflowY: "auto" }}>
            {files.map(f => (
              <Box
                key={f.name}
                sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  p: 0.8, border: "1px solid", borderColor: "divider", borderRadius: 1,
                }}
              >
                <AudiotrackIcon fontSize="small" color="success" />
                <Typography variant="body2" sx={{ flex: 1 }} noWrap title={f.name}>{f.name}</Typography>
                <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: "nowrap" }}>
                  {(f.size / 1024).toFixed(0)} KB
                </Typography>
                <IconButton size="small" onClick={() => removeFile(f.name)} disabled={uploading}>
                  <DeleteOutlineIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={(progress.done / progress.total) * 100} />
            <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
              {progress.done} de {progress.total} áudios enviados…
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>Cancelar</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleUpload}
          disabled={uploading || files.length === 0}
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
        >
          {uploading ? "Importando…" : `Importar ${files.length} áudio(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickAnswersBulkAudioModal;
